/**
 * UUID generation composable with RFC 4122 compliant fallback
 *
 * Uses native crypto.randomUUID() when available, falls back to
 * proper UUID v4 format for database compatibility (offline orders)
 */
export const useUUID = () => {
  /**
   * Generate RFC 4122 compliant UUID v4 fallback
   * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
   */
  const generateUUIDv4Fallback = (): string => {
    const chars = '0123456789abcdef'
    let uuid = ''

    for (let i = 0; i < 36; i++) {
      if (i === 8 || i === 13 || i === 18 || i === 23) {
        uuid += '-'
      }
      else if (i === 14) {
        uuid += '4' // UUID version 4
      }
      else if (i === 19) {
        // Bits 6-7 of clock_seq_hi_and_reserved to 01
        uuid += chars[Math.floor(Math.random() * 4) + 8] // 8, 9, a, or b
      }
      else {
        uuid += chars[Math.floor(Math.random() * 16)]
      }
    }

    return uuid
  }

  /**
   * Generate a UUID using native browser API with compliant fallback
   */
  const generate = (): string => {
    // Use native browser API if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }

    // RFC 4122 compliant UUID v4 fallback for database compatibility
    return generateUUIDv4Fallback()
  }

  /**
   * Validate if a string is a valid UUID format
   */
  const isValid = (uuid: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    return uuidRegex.test(uuid)
  }

  /**
   * Generate a UUID specifically for cart items
   * Semantic alias for generate()
   */
  const generateCartUUID = generate

  return {
    generate,
    generateCartUUID,
    isValid,
  }
}
